import React, { forwardRef, memo, useImperativeHandle } from 'react';

import { useToast } from '@chakra-ui/toast';
import { useDisclosure } from '@chakra-ui/hooks';

import { useSKU } from 'hooks/useSKU';

import { useAppSelector } from 'store/hooks';
import { locationSelector } from 'store/slices/washhub';

import { uploadInventoryItem } from 'api/washhub';
import { useMutation, useQueryClient } from 'react-query';

import { RefType, UploadCSVModalProps } from './UploadCSVModal.props';

import UploadCSVModalView from './UploadCSVModal.view';

const Container = forwardRef<RefType, UploadCSVModalProps>((props, ref) => {
  const { type } = props;

  const toast = useToast();
  const queryClient = useQueryClient();

  const selectedWashHub = useAppSelector(locationSelector);

  const queryKey = `fetch-${type}s-${selectedWashHub?._id}`;

  const { isOpen, onOpen, onClose } = useDisclosure();

  const { data: skus = [], isLoading: isLoadingSku } = useSKU(type);

  const { mutate, isLoading } = useMutation((payload: any) =>
    uploadInventoryItem(payload, type),
  );
  const getSkuByWashhubId = () => {
    const _skus = skus?.filter((i) => selectedWashHub?._id === i.washHub);
    return _skus;
  };

  useImperativeHandle(
    ref,
    () => ({
      onOpen,
    }),
    [onOpen],
  );

  const onUpload = async (file: File, values: any, onSuccess: () => void) => {
    const sku = values?.sku;
    const skuCode = getSkuByWashhubId()?.find((s) => s._id === sku)?.code || '';

    await mutate(
      {
        sku,
        skuCode,
        file,
        washHub: selectedWashHub?._id,
      },
      {
        onSuccess: (res: any) => {
          const items = res?.item?.length || '';

          toast({
            title: 'Success',
            status: 'success',
            isClosable: true,
            duration: 10000,
            description: `${items} ${type}s uploaded successfully`,
          });

          queryClient.invalidateQueries(queryKey, {
            exact: true,
          });

          onClose();
          onSuccess();
        },
      },
    );
  };

  const _props = {
    type,
    skus: getSkuByWashhubId(),
    isOpen,
    onClose,
    onUpload,
    isLoading,
    isLoadingSku,
  };

  return <UploadCSVModalView {..._props} />;
});

const UploadCSVModal = memo(Container);

export default UploadCSVModal;
